import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingCart, ArrowLeft, Tag, X, Check } from "lucide-react";
import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useCart } from "@/hooks/use-cart";
import { usePromo } from "@/hooks/use-promo";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Panier — angle-shop.com" },
      { name: "description", content: "Consultez et gérez votre panier angle-shop.com." },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { items, updateQuantity, removeItem, totalPrice, totalItems } = useCart();
  const { appliedCode, discountRate, alreadyUsed, applyCode, removeCode } = usePromo();
  const [codeInput, setCodeInput] = useState("");
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);

  const discountAmount = totalPrice * discountRate;
  const subtotalAfterDiscount = totalPrice - discountAmount;
  const shippingFree = subtotalAfterDiscount >= 59;
  const shippingCost = shippingFree ? 0 : 4.90;

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    const result = applyCode(codeInput);
    setFeedback(result);
    if (result.ok) setCodeInput("");
  };

  if (items.length === 0) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">Votre panier est vide</h2>
          <p className="text-muted-foreground mt-2">Découvrez nos produits pour habiller votre style</p>
          <Link to="/catalog" className="inline-flex items-center px-6 py-3 mt-8 rounded-xl bg-gradient-electric text-electric-foreground font-semibold shadow-electric hover:opacity-90 transition-opacity">
            Voir le catalogue
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/catalog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Continuer mes achats
        </Link>

        <h1 className="font-display text-3xl font-bold text-foreground tracking-tight mb-8">
          Panier ({totalItems} {totalItems > 1 ? "articles" : "article"})
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {items.map((item, i) => (
              <motion.div
                key={item.product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-card rounded-2xl p-4 sm:p-6 shadow-card flex gap-4 sm:gap-6"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">{item.product.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{item.product.shortDescription}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeItem(item.product.id)} className="ml-2 p-2 rounded-lg hover:bg-destructive/10 transition-colors">
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                    <span className="font-bold text-foreground">{(item.product.price * item.quantity).toFixed(2)}€</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card rounded-2xl p-6 shadow-card sticky top-24">
              <h2 className="font-display font-bold text-lg text-foreground mb-6">Récapitulatif</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-medium text-foreground">{totalPrice.toFixed(2)}€</span>
                </div>
                {appliedCode && (
                  <div className="flex justify-between text-badge-success">
                    <span className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" /> Code {appliedCode} (-5%)
                    </span>
                    <span className="font-medium">-{discountAmount.toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Livraison</span>
                  <span className="font-medium text-foreground">{shippingFree ? "Offerte" : `${shippingCost.toFixed(2)}€`}</span>
                </div>
                {!shippingFree && (
                  <p className="text-xs text-electric">Plus que {(59 - subtotalAfterDiscount).toFixed(2)}€ pour la livraison offerte</p>
                )}
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-lg text-foreground">{(subtotalAfterDiscount + shippingCost).toFixed(2)}€</span>
                </div>
              </div>

              {/* Promo code section */}
              <div className="mt-5 pt-5 border-t border-border">
                {appliedCode ? (
                  <div className="flex items-center justify-between bg-badge-success/10 rounded-xl px-3 py-2.5">
                    <span className="flex items-center gap-2 text-sm font-medium text-badge-success">
                      <Check className="w-4 h-4" /> Code {appliedCode} appliqué
                    </span>
                    <button
                      onClick={() => { removeCode(); setFeedback(null); }}
                      className="p-1 rounded hover:bg-badge-success/20 transition-colors"
                      aria-label="Retirer le code"
                    >
                      <X className="w-4 h-4 text-badge-success" />
                    </button>
                  </div>
                ) : alreadyUsed ? (
                  <p className="text-xs text-muted-foreground text-center">
                    Vous avez déjà utilisé votre code promo.
                  </p>
                ) : (
                  <form onSubmit={handleApply} className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" /> Code promo
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        placeholder="SUMMERS"
                        className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-electric/40"
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
                      >
                        Appliquer
                      </button>
                    </div>
                    {feedback && !feedback.ok && (
                      <p className="text-xs text-destructive">{feedback.message}</p>
                    )}
                  </form>
                )}
              </div>
              <Link
                to="/checkout"
                className="mt-6 w-full flex items-center justify-center px-6 py-4 rounded-xl bg-gradient-electric text-electric-foreground font-semibold shadow-electric hover:opacity-90 transition-opacity"
              >
                Passer la commande
              </Link>
              <p className="text-xs text-muted-foreground text-center mt-3">Paiement sécurisé · Mode test</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
